import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ServiceContext from './components/ServiceContext';
import QuotationDetails from './components/QuotationDetails';
import AddOnsExtras from './components/AddOnsExtras';
import './style.css';
import { formatDate } from '@/utils/dateUtils';
import { CreateQuotation } from '@/api/endpoints/Requests';
import { errorToast } from '@/utils/toasters';
import ActionBtn from '@/components/ActionBtn';

const AddQuotation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const requestId = location?.state?.requestId;
    const patientName = location?.state?.patientName || 'Johnathan Doe';
    const serviceContext = location?.state?.serviceContext;

    const [quotationData, setQuotationData] = useState({
        proposedTreatmentDescription: '',
        proposedInpatientDateRange: { start: '', end: '' },
        includedInPackage: [],
        postOperativeConsultations: 1,
        expectedLengthOfStay: { days: '', hours: '' },
        baseQuotationPrice: '',
    });

    const [addOns, setAddOns] = useState([]);

    const handleBack = () => {
        navigate('/panel/provider/requests/medical');
    };

    const handleSaveDraft = () => {

        if(quotationData?.proposedTreatmentDescription === '' 
            || quotationData?.expectedLengthOfStay?.days === '' 
            || quotationData?.baseQuotationPrice === ''
            || quotationData?.proposedInpatientDateRange?.value?.from === null
            || quotationData?.proposedInpatientDateRange?.value?.to === null
            || quotationData?.includedInPackage?.length === 0
            || addOns?.length === 0)
            return errorToast('error-validation');

        const data = {
            treatmentDescription: quotationData?.proposedTreatmentDescription,
            expectedLengthOfStay: quotationData?.expectedLengthOfStay?.days || 1,
            totalPrice: quotationData?.baseQuotationPrice || 0,
            availableFrom: quotationData?.proposedInpatientDateRange?.value?.from || null,
            availableTo: quotationData?.proposedInpatientDateRange?.value?.to || null,
            packages: quotationData?.includedInPackage?.map(item => item.description) || [],
            addOns: addOns?.map(item => ({ ...item, price: item.cost })) || [],
        };

        setIsLoading(true);
        CreateQuotation(requestId, data).then(res => {
            navigate('/panel/provider/requests/medical');
            setIsLoading(false);
            successToast('operation-done-successfuly');
        }).catch(err => {
            setIsLoading(false);
            console.log(err);
        });
    };

    const handleQuotationChange = (field, value) => {
        setQuotationData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAddOn = (addon) => {
        setAddOns(prev => [...prev, addon]);
    };

    const handleRemoveAddOn = (index) => {
        setAddOns(prev => prev.filter((_, i) => i !== index));
    };

    const calculateTotal = () => {
        const basePrice = parseFloat(quotationData.baseQuotationPrice) || 0;
        const addOnsTotal = addOns.reduce((sum, addon) => sum + (parseFloat(addon.cost) || 0), 0);
        return (basePrice + addOnsTotal).toFixed(2);
    };



    return (
        <div className="add-quotation-container">
            {/* Header */}
            <div className="add-quotation-header">
                <div className="add-quotation-header-left">
                    <button className="add-quotation-back-btn" onClick={handleBack}>
                        <span className="add-quotation-back-icon">{i18n.language === 'ar' ? '→' : '←'}</span>
                        <span>{t('back')}</span>
                    </button>
                    <div className="add-quotation-title-section">
                        <h1 className="add-quotation-title">{t('create-quotation')}</h1>
                        <p className="add-quotation-subtitle">
                            {t('generate-comprehensive-medical-service-quotation')} {requestId}
                        </p>
                    </div>
                </div>
                <div className="add-quotation-header-right">
                    <button disabled={isLoading} className="add-quotation-cancel-btn" onClick={handleBack}>
                        {t('Cancel')}
                    </button>
                    <ActionBtn  onApply={handleSaveDraft} title={isLoading ? "loading" : "Save"} />

                </div>
            </div>

            {/* Patient Info Bar */}
            <div className="add-quotation-patient-info">
                <div className="add-quotation-patient-avatar">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div className="add-quotation-patient-details">
                    <div className="add-quotation-patient-label">{t('patient-name')}:</div>
                    <div className="add-quotation-patient-name">{patientName}</div>
                </div>
                <div className="add-quotation-patient-details">
                    <div className="add-quotation-patient-label">{t('age')}:</div>
                    <div className="add-quotation-patient-name">{serviceContext?.age || '34 years'}</div>
                </div>
            </div>

            {/* Main Content */}
            <div className="add-quotation-content">
                <ServiceContext data={serviceContext} />

                <QuotationDetails
                    data={quotationData}
                    onChange={handleQuotationChange}
                />

                <AddOnsExtras
                    addOns={addOns}
                    onAdd={handleAddOn}
                    onRemove={handleRemoveAddOn}
                    total={calculateTotal()}
                />
            </div>
        </div>
    );
};

export default AddQuotation;

